import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';

const DATE_INPUT_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MIN_DATE_YEAR = 1000;

const formatDateForInput = (date) => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  if (d.getFullYear() < MIN_DATE_YEAR) {
    return '';
  }

  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

const parseDateInputValue = (value) => {
  if (!DATE_INPUT_PATTERN.test(value)) {
    return null;
  }

  const [year, month, day] = value.split('-').map(Number);
  // Chrome can temporarily pad a partially edited year (for example, "2")
  // to "0002". Treat that as incomplete rather than committing it upstream.
  if (year < MIN_DATE_YEAR) {
    return null;
  }
  const date = new Date(0);
  date.setHours(0, 0, 0, 0);
  date.setFullYear(year, month - 1, day);

  // Date normalises invalid values (for example, 31 April), so verify that
  // the resulting date still matches the value entered by the user.
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

const DateRangeSelector = ({ 
  startDate, 
  endDate, 
  onDateChange 
}) => {
  // Keep the browser's in-progress value locally. Native date inputs can
  // emit an empty value while the user is editing one of their date segments.
  // Updating the parent with that incomplete value would immediately reset
  // the controlled input back to its previous formatted date.
  const [startDateValue, setStartDateValue] = useState(() => formatDateForInput(startDate));
  const [endDateValue, setEndDateValue] = useState(() => formatDateForInput(endDate));

  useEffect(() => {
    setStartDateValue(formatDateForInput(startDate));
  }, [startDate]);

  useEffect(() => {
    setEndDateValue(formatDateForInput(endDate));
  }, [endDate]);

  // Handle date input changes
  const handleStartDateChange = (e) => {
    const value = e.target.value;
    const newStartDate = parseDateInputValue(value);
    if (!newStartDate) {
      // Native date inputs report an empty value while a date segment is still
      // being edited. Do not write that transient value back to the controlled
      // input or Chrome will clear the segment the user just entered.
      return;
    }

    setStartDateValue(value);
    onDateChange({
      selection: {
        startDate: newStartDate,
        endDate: endDate,
        key: 'selection'
      }
    });
  };

  const handleEndDateChange = (e) => {
    const value = e.target.value;
    const newEndDate = parseDateInputValue(value);
    if (!newEndDate) {
      // See the start date handler: preserve incomplete native date edits.
      return;
    }

    setEndDateValue(value);
    onDateChange({
      selection: {
        startDate: startDate,
        endDate: newEndDate,
        key: 'selection'
      }
    });
  };

  const restoreStartDate = (e) => {
    if (!parseDateInputValue(e.currentTarget.value)) {
      setStartDateValue(formatDateForInput(startDate));
    }
  };

  const restoreEndDate = (e) => {
    if (!parseDateInputValue(e.currentTarget.value)) {
      setEndDateValue(formatDateForInput(endDate));
    }
  };

  // Handle shortcut buttons
  const moveEarlier = () => {
    const newStartDate = new Date(startDate);
    newStartDate.setDate(newStartDate.getDate() - 14);
    
    onDateChange({
      selection: {
        startDate: newStartDate,
        endDate: endDate,
        key: 'selection'
      }
    });
  };

  const moveLater = () => {
    const newEndDate = new Date(endDate);
    newEndDate.setDate(newEndDate.getDate() + 14);
    
    onDateChange({
      selection: {
        startDate: startDate,
        endDate: newEndDate,
        key: 'selection'
      }
    });
  };

  const resetDates = () => {
    const now = new Date();
    const newStartDate = new Date(now);
    newStartDate.setDate(now.getDate() - 14);
    
    const newEndDate = new Date(now);
    newEndDate.setDate(now.getDate() + 14);
    
    onDateChange({
      selection: {
        startDate: newStartDate,
        endDate: newEndDate,
        key: 'selection'
      }
    });
  };

  return (
    <div className="date-range-selector d-flex align-items-center">
      <Button variant="outline-secondary" onClick={moveEarlier} className="me-1">
        &lt;&lt;
      </Button>
      <Form.Control
        type="date"
        aria-label="Start date"
        value={startDateValue}
        onChange={handleStartDateChange}
        onBlur={restoreStartDate}
        style={{ width: '130px' }}
      />
      <span className="mx-1">-</span>
      <Form.Control
        type="date"
        aria-label="End date"
        value={endDateValue}
        onChange={handleEndDateChange}
        onBlur={restoreEndDate}
        style={{ width: '130px' }}
      />
      <Button variant="outline-secondary" onClick={moveLater} className="mx-1">
        &gt;&gt;
      </Button>
      <Button variant="outline-secondary" onClick={resetDates} title="Reset dates" className="px-2">
        ✕
      </Button>
    </div>
  );
};

export default DateRangeSelector;
